/**
 * Bootstrap flags — temporary, and deliberately loud about it.
 *
 * These exist to solve a genuine cold-start problem: nobody can validate an account
 * until an administrator exists, and an administrator cannot exist until somebody
 * validates their account. Until the first superuser is seeded, the portal has no
 * way in.
 *
 * Both flags are **off by default** and both **weaken security when on**, so each one
 * announces itself in the console on every load. They are read once here rather than
 * scattered through the code, so turning them back off is a single edit to
 * `.env.local` with nothing left behind.
 */

const isOn = (raw: string | undefined): boolean =>
  (raw ?? '').trim().toLowerCase() === 'true';

/**
 * Skips the "pendiente de validación por RR.HH." step.
 *
 * With this on, any account that passes the corporate-domain gate becomes an active
 * session immediately, with the role the `people` record carries (or `empleado` if
 * there is no record yet).
 *
 * **What it gives up:** the guarantee that a stranger with a corporate address sees
 * nothing until a human vouches for them. On a domain where anyone can be issued an
 * address — a contractor, an intern, a former employee whose account is still live —
 * that guarantee is the difference between "authenticated" and "authorised". Turn it
 * back off as soon as the first administrator exists.
 *
 * **It does not** weaken the domain gate: a non-corporate account is still rejected
 * at sign-in by the blocking function and by the Firestore rules.
 */
export const skipAccountValidation = (): boolean =>
  isOn(import.meta.env.VITE_SKIP_ACCOUNT_VALIDATION);

/**
 * Runs the app against in-memory data instead of Firestore.
 *
 * For reviewing the UI before a Firebase project exists. Nothing persists, no network
 * call is made, and every screen renders against the handoff's own sample data.
 */
export const demoMode = (): boolean => isOn(import.meta.env.VITE_DEMO_MODE);

/**
 * Offers password sign-in beside «Continuar con Google».
 *
 * For QA and demonstrations. Three testers need a session as an employee, a
 * leader and an administrator, and issuing three corporate mailboxes to get them
 * is a licence bill for accounts nobody will ever read mail with. The accounts
 * are created out of band by `scripts/create-test-users.ts`.
 *
 * **What it gives up:** «no hay contraseñas que recordar» stops being true, and
 * the portal grows a second door. What it does *not* give up is the boundary
 * itself — the address must still be on an allowed domain and still verified,
 * both checked by the same blocking function that guards Google. A stranger who
 * signs themselves up at a corporate address arrives unverified and is refused.
 *
 * It is a build flag rather than runtime configuration on purpose: the login
 * screen renders before anybody is authenticated, and `config/*` is unreadable
 * to a client with no session. Turning it off is a rebuild, which is the honest
 * cost of a door that exists in the bundle.
 */
export const passwordLogin = (): boolean => isOn(import.meta.env.VITE_PASSWORD_LOGIN);

/** Prints a visible warning for whichever flags are on. Called once at startup. */
export const announceBootstrapFlags = (): void => {
  if (demoMode()) {
    console.warn(
      '%c[MODO DEMO]%c Datos en memoria, sin Firebase. Nada se guarda. ' +
        'Apaga VITE_DEMO_MODE para conectar a Firestore.',
      'background:#7C5CFF;color:#fff;padding:2px 6px;border-radius:4px;font-weight:700',
      'color:#5B3FD4',
    );
  }

  if (skipAccountValidation()) {
    console.warn(
      '%c[VALIDACIÓN DESACTIVADA]%c Cualquier cuenta del dominio corporativo entra ' +
        'sin que RR.HH. la valide. Es un arranque en frío, no una configuración de ' +
        'producción: vuelve a poner VITE_SKIP_ACCOUNT_VALIDATION=false en cuanto ' +
        'exista el primer administrador.',
      'background:#E5484D;color:#fff;padding:2px 6px;border-radius:4px;font-weight:700',
      'color:#A82B30',
    );
  }

  if (passwordLogin()) {
    console.warn(
      '%c[ACCESO CON CONTRASEÑA]%c El portal ofrece una segunda puerta además de ' +
        'Google, para las cuentas de prueba de QA. El cerco de dominio sigue en pie. ' +
        'Vuelve a poner VITE_PASSWORD_LOGIN=false y reconstruye cuando termine QA.',
      'background:#B36B00;color:#fff;padding:2px 6px;border-radius:4px;font-weight:700',
      'color:#8A5200',
    );
  }
};
